'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import posthog from 'posthog-js';
import { v4 as uuidv4 } from 'uuid';
import { useApplicationContext } from '../../../contexts/ApplicationContext';
import { useSnackbar } from '@context/SnackbarContext';
import { Button } from '@components/ui/Button';
import { TextField } from '@components/ui/TextField';
import {
  PreQualifyFormSchema,
  PreQualifyFormValues,
} from '@components/forms/PreQualifyForm/schemas/preQualifyFormSchema';
import { FormWrapper } from '@components/ui/FormWrapper';
import Typography from '@components/ui/Typography';
import React, { useRef, useImperativeHandle, forwardRef } from 'react';

import PreQualifyFormFields from '@enums/preQualifyForm';

import config from '@config';
import DataDogInstance from '@utils/monitoring';
import { mapToMarketingCaptureType } from '@utils/mappers/mapToMarketingCaptureType';
import { SubmitForm } from '@services/FormService';
import { submitMarketingCapture } from '@services/marketingCaptureService';
import { FormType } from 'src/types/FormType';

interface PreQualifyFormProps {
  source: string;
  showNote?: boolean;
  buttonText?: string;
}

export interface PreQualifyFormRef {
  focusFirstName: () => void;
}

const PreQualifyForm = forwardRef<PreQualifyFormRef, PreQualifyFormProps>(
  ({ source, showNote = true, buttonText = 'SEE IF I PRE-QUALIFY' }, ref) => {
    const { applicationId, referrerId, referrerIdType } =
      useApplicationContext();
    const firstNameInputRef = useRef<HTMLInputElement | null>(
      null
    ) as React.MutableRefObject<HTMLInputElement | null>;

    useImperativeHandle(ref, () => ({
      focusFirstName: () => {
        setTimeout(() => {
          firstNameInputRef.current?.focus();
        }, 300);
      },
    }));

    const form = useForm<PreQualifyFormValues>({
      resolver: zodResolver(PreQualifyFormSchema),
      defaultValues: {
        [PreQualifyFormFields.FirstName]: '',
        [PreQualifyFormFields.LastName]: '',
        [PreQualifyFormFields.Email]: '',
      },
    });

    const showSnackbar = useSnackbar();

    const onSubmit = async (data: PreQualifyFormValues) => {
      const prospectId = uuidv4();

      try {
        posthog.capture('prequalify_form_submitted', {
          ...data,
          timestamp: new Date().toISOString(),
        });

        posthog.people.set({
          firstName: data[PreQualifyFormFields.FirstName],
          lastName: data[PreQualifyFormFields.LastName],
          email: data[PreQualifyFormFields.Email],
        });

        const mappedData = {
          f: data[PreQualifyFormFields.FirstName],
          l: data[PreQualifyFormFields.LastName],
          e: data[PreQualifyFormFields.Email],
          ap: applicationId,
          d: posthog.get_distinct_id(),
        };

        const encodedData = btoa(JSON.stringify(mappedData));

        const formData: FormType = {
          firstName: data[PreQualifyFormFields.FirstName],
          lastName: data[PreQualifyFormFields.LastName],
          email: data[PreQualifyFormFields.Email],
          source,
        };

        if (config.ENVIRONMENT !== 'production') {
          DataDogInstance.getInstance().logInfo(
            '[PreQualifyForm] applicationId',
            { applicationId }
          );
        }

        await SubmitForm(formData);

        try {
          await submitMarketingCapture(
            mapToMarketingCaptureType(data, prospectId, applicationId, {
              referrerId,
              referrerIdType,
            })
          );
        } catch (captureError) {
          // The capture event feeds the marketing warehouse; losing it must
          // not keep the user out of the application flow.
          DataDogInstance.getInstance().logError(
            'Failed to capture pre-qualify submission',
            { prospectId, source },
            captureError as Error
          );
        }

        // The referral travels as plain params, alongside prospectId rather
        // than inside the cgn-data blob, which is decoded on the consent-gated
        // PostHog identity path in the app flow. The names match the ones the
        // referral link arrives with — never `cgn_aid`, which is the
        // advertiser id the track store files hits under.
        const referrerParams = referrerId
          ? `&aid=${encodeURIComponent(referrerId)}${
              referrerIdType ? `&src=${encodeURIComponent(referrerIdType)}` : ''
            }`
          : '';

        // The app flow is served by a different app behind an Amplify proxy,
        // so this must be a full page load. router.push would treat the
        // same-origin coign.com/applytospendright URL as an App Router route
        // and 404 client-side.
        window.location.assign(
          `${config.APPFLOW_URL}?cgn-data=${encodedData}&prospectId=${prospectId}${referrerParams}`
        );
      } catch (error) {
        DataDogInstance.getInstance().logError(
          'Failed to submit pre-qualify form',
          { data, appflowUrl: config.APPFLOW_URL },
          error as Error
        );
        showSnackbar({
          message: 'Failed to submit form. Please try again.',
          error: true,
        });
      }
    };

    const createFieldProps = (
      name: string,
      inputRef?: React.MutableRefObject<HTMLInputElement | null>
    ) => {
      const register = form.register(name);
      return {
        ...register,
        ref: inputRef
          ? (e: HTMLInputElement | null) => {
              register.ref(e);
              if (inputRef && e) {
                inputRef.current = e;
              }
            }
          : register.ref,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          register.onChange(e);
          try {
            posthog.capture('prequalify_input_changed', {
              field_name: name,
              field_value: e.target.value,
              timestamp: new Date().toISOString(),
            });
          } catch (error) {
            DataDogInstance.getInstance().logError(
              'Failed to capture pre-qualify input change',
              { name, value: e.target.value },
              error as Error
            );
          }
        },
      };
    };

    return (
      <FormWrapper
        id="pre-qualify-form"
        form={form}
        className="flex w-full flex-col"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="mb-6 flex flex-col gap-6 sm:flex-row sm:gap-4">
          <TextField
            className="w-full"
            label="First name"
            required
            dark
            {...createFieldProps(
              PreQualifyFormFields.FirstName,
              firstNameInputRef
            )}
          />
          <TextField
            className="w-full"
            label="Last name"
            required
            dark
            {...createFieldProps(PreQualifyFormFields.LastName)}
          />
        </div>
        <TextField
          className="w-full"
          label="Email"
          required
          dark
          {...createFieldProps(PreQualifyFormFields.Email)}
        />
        <Button
          id="pre-qualify-form-button"
          type="submit"
          variant="secondary"
          className="mx-auto mb-4 mt-10 w-full gap-1 rounded-[50px] sm:w-[254px]"
        >
          {buttonText} {showNote && <span className="p-0 font-sans">⁴</span>}
        </Button>
        {showNote && (
          <Typography
            intent="bodyXSmall"
            align="center"
            className="mx-auto text-white sm:w-[410px]"
          >
            Get started today with no impact to your credit score. If you
            pre-qualify, you&apos;ll be invited to complete your application. If
            you choose to move forward with your application, your credit score
            may be impacted. It&apos;s quick, simple, and safe.
          </Typography>
        )}
      </FormWrapper>
    );
  }
);

PreQualifyForm.displayName = 'PreQualifyForm';

export default PreQualifyForm;
